import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, X } from 'lucide-react';

interface HomeNavbarProps {
  onCTA: () => void;
}

const HomeNavbar = ({ onCTA }: HomeNavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  // Show pricing link only on /business page
  const showPricing = location.pathname === '/business';
  const isBusinessPage = location.pathname === '/business';

  const navLinks = [
    { label: 'Features', href: '#features' },
    { label: 'How it works', href: '#how-it-works' },
    ...(showPricing ? [{ label: 'Pricing', href: '#pricing' }] : []),
    { label: 'Testimonials', href: '#testimonials' }
  ];

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setIsOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--page-bg)] border-b border-border backdrop-blur-sm bg-[var(--page-bg)]/90">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <span className="text-[16px] font-semibold text-[var(--text-primary)] tracking-normal">
              Zentrix Insights
            </span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <a
                key={link.label}
                href={link.href}
                className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-[13px] font-medium"
                onClick={(e) => handleNavClick(e, link.href.slice(1))}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="h-9 px-4 text-[13px] font-semibold border-2 border-border hover:border-[var(--text-primary)] rounded-lg transition-all duration-200"
            >
              Sign in
            </Button>
            <Button onClick={isBusinessPage ? () => navigate('/signup') : onCTA} className="hidden sm:inline-flex h-10 px-6 text-[13px] font-semibold bg-[var(--accent-hex)] hover:bg-[var(--accent-hover)] text-white rounded-lg transition-all duration-200">
              Get started
            </Button>

            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="h-10 w-10">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                <div className="flex flex-col gap-6 mt-8">
                  {navLinks.map(link => (
                    <a
                      key={link.label}
                      href={link.href}
                      className="text-[16px] font-medium text-[var(--text-primary)] hover:text-[var(--text-secondary)] transition-colors"
                      onClick={(e) => handleNavClick(e, link.href.slice(1))}
                    >
                      {link.label}
                    </a>
                  ))}
                  <div className="border-t border-border pt-6 space-y-3">
                    <Button
                      onClick={() => {
                        setIsOpen(false);
                        isBusinessPage ? navigate('/signup') : onCTA();
                      }}
                      className="w-full bg-[var(--accent-hex)] hover:bg-[var(--accent-hover)] text-white"
                    >
                      Get started
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default HomeNavbar;
